import { useRef, useCallback } from 'react';
import { stopAudio } from '../api/chat';

/**
 * Custom hook for managing audio playback with abort control
 * Centralizes all abort error handling and provides clean API
 */
export const useAudioController = () => {
  const abortControllerRef = useRef(new AbortController());

  // Create a new abort controller and return its signal
  const createNewController = useCallback(() => {
    try {
      abortControllerRef.current = new AbortController();
      return abortControllerRef.current.signal;
    } catch (error) {
      console.warn('Failed to create AbortController, creating fallback');
      // Create a fallback controller if creation fails
      abortControllerRef.current = { 
        signal: { aborted: false }, 
        abort: () => {} 
      };
      return abortControllerRef.current.signal;
    }
  }, []);

  // Safely abort the current controller
  const abortCurrent = useCallback(() => {
    try {
      if (abortControllerRef.current?.abort) {
        abortControllerRef.current.abort();
      }
    } catch (error) {
      // Abort errors are expected and should be silent
    }
  }, []);

  // Stop all audio and abort current operations
  const stopAllAudio = useCallback(() => {
    abortCurrent();
    
    try {
      stopAudio();
    } catch (error) {
      // Stop audio errors should be silent
    }
  }, [abortCurrent]);

  // Execute audio operation with proper error handling
  const executeAudioOperation = useCallback(async (audioOperation, onSuccess, onError) => {
    // Stop any current audio
    stopAllAudio();
    
    // Create new controller
    const signal = createNewController();
    
    // Add a small delay to prevent race conditions
    await new Promise(resolve => setTimeout(resolve, 10));
    
    // Check if already aborted before starting
    if (signal.aborted) {
      return null;
    }
    
    // Wrap in a promise that NEVER rejects for abort errors
    return new Promise((resolve) => {
      audioOperation(signal)
        .then((result) => {
          // Check if aborted after operation
          if (signal.aborted) {
            resolve(null);
            return;
          }
          
          if (onSuccess) {
            try {
              onSuccess(result);
            } catch (err) {
              // Even success callbacks can throw, handle them
            }
          }
          resolve(result);
        })
        .catch((error) => {
          // Handle abort errors silently - NEVER reject for these
          if (error.name === 'AbortError' || 
              error.message?.includes('abort') ||
              error.message?.includes('signal is aborted') ||
              error.message?.includes('without reason')) {
            // Silently resolve instead of rejecting
            resolve(null);
            return;
          }
          
          // Handle other errors
          if (onError) {
            try {
              onError(error);
            } catch (err) {
              // Prevent error handler from throwing
            }
          }
          
          // For non-abort errors, still resolve to prevent runtime errors
          resolve(null);
        });
    });
  }, [stopAllAudio, createNewController]);

  return {
    executeAudioOperation,
    stopAllAudio,
    abortCurrent,
    getSignal: () => abortControllerRef.current?.signal
  };
};

// Global error handler for unhandled abort errors
export const setupGlobalAbortErrorHandler = () => {
  const handleUnhandledRejection = (event) => {
    const reason = event.reason;
    const message = reason?.message || reason?.toString() || '';
    
    // Check for various abort error patterns
    const isAbortError = 
      reason?.name === 'AbortError' || 
      message.includes('abort') ||
      message.includes('signal is aborted') ||
      message.includes('without reason') ||
      message.includes('Audio playback aborted') ||
      message.includes('Audio fetch aborted');
    
    if (isAbortError) {
      event.preventDefault(); // Prevent console error
      console.debug('Suppressed abort error:', message); // Optional debug log
    }
  };

  const handleError = (event) => {
    const error = event.error;
    const message = error?.message || error?.toString() || '';
    
    const isAbortError = 
      error?.name === 'AbortError' || 
      message.includes('abort') ||
      message.includes('signal is aborted');
    
    if (isAbortError) {
      event.preventDefault();
      console.debug('Suppressed error event:', message);
    }
  };

  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  window.addEventListener('error', handleError);
  
  return () => {
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    window.removeEventListener('error', handleError);
  };
};